import { lt } from './localized'
import type { LocalizedText, WorkstationDef, WorkstationId } from './types'

/** Resolve the card/detail surtitle for the workstation’s current upgrade level (clamped 1–3). */
export function workstationSurtitleAtLevel(def: WorkstationDef, level: number): LocalizedText {
  const idx = Math.min(Math.max(Math.floor(level) || 1, 1), 3) - 1
  return def.cardSurtitles[idx]
}

export const WORKSTATIONS: Record<WorkstationId, WorkstationDef> = {
  base_station: {
    id: 'base_station',
    name: lt('Basisplaats', 'Base station'),
    cardSurtitles: [
      lt('Schuilplaats', 'Hideout'),
      lt('Buitenpost', 'Outpost'),
      lt('Hoofdkwartier', 'Headquarters'),
    ],
    glyph: '🗺️',
    blurb: lt(
      'Een boomstronk, een rugzak en een papieren kaart. Hier beginnen missies.',
      'Central camp coordination',
    ),
    kind: 'hub',
  },
  stash: {
    id: 'stash',
    name: lt('Opslag', 'Stash'),
    cardSurtitles: [
      lt('Rugzak', 'Backpack'),
      lt('Kist', 'Chest'),
      lt('Opslagschuur', 'Storage shed'),
    ],
    glyph: '🎒',
    blurb: lt(
      'Georganiseerde voorraadopslag',
      'Organised supply storage',
    ),
    kind: 'storage',
  },
  kitchen: {
    id: 'kitchen',
    name: lt('Keuken', 'Kitchen'),
    cardSurtitles: [
      lt('Kampvuur', 'Campfire'),
      lt('Grill', 'Grill'),
      lt('Keukenblok', 'Full kitchen'),
    ],
    glyph: '🍲',
    blurb: lt(
      'Een gedeukte gasbrander en wat verroeste pannen.',
      'Cooking setup for your team.',
    ),
    kind: 'restoreNeed',
    need: 'hunger',
    category: 'food',
    needRestore: 35,
    buttonLabel: lt('Kook een maaltijd', 'Cook a meal'),
    emptyLabel: lt('Geen voedsel', 'No food'),
  },
  well: {
    id: 'well',
    name: lt('Waterpunt', 'Water point'),
    cardSurtitles: [
      lt('Waterton', 'Rain barrel'),
      lt('Handpomp', 'Hand pump'),
      lt('Zuiveringsstation', 'Filtration post'),
    ],
    glyph: '💧',
    blurb: lt(
      'Bron van zuiver water',
      'Source of clean water.',
    ),
    kind: 'restoreNeed',
    need: 'thirst',
    category: 'water',
    needRestore: 35,
    buttonLabel: lt('Schenk water in', 'Pour water'),
    emptyLabel: lt('Geen water', 'No water'),
  },
  infirmary: {
    id: 'infirmary',
    name: lt('Medische post', 'Infirmary'),
    cardSurtitles: [
      lt('EHBO-tent', 'Medic tent'),
      lt('Veldkliniek', 'Field clinic'),
      lt('Ziekenboeg', 'Sick bay'),
    ],
    glyph: '⚕',
    blurb: lt(
      'Behandel verwondingen hier.',
      'Treat injuries here.',
    ),
    kind: 'injuryCare',
    category: 'meds',
    buttonLabel: lt('Behandel verwonding', 'Treat injury'),
    emptyLabel: lt('Geen medicijnen', 'No medicine'),
  },
  lodging: {
    id: 'lodging',
    name: lt('Onderdak', 'Lodging'),
    cardSurtitles: [
      lt('Tent', 'Tent'),
      lt('Herberg', 'Lodge'),
      lt('Kazerne', 'Barracks'),
    ],
    glyph: '🏠',
    blurb: lt(
      'Meer slaapplaatsen voor het team.',
      'Beds for your team.',
    ),
    kind: 'lodging',
  },
  broadcast_station: {
    id: 'broadcast_station',
    name: lt('Zendstation', 'Communication center'),
    cardSurtitles: [
      lt('Radiohut', 'Radio shack'),
      lt('Relais', 'Relay'),
      lt('Zendnetwerk', 'Broadcast grid'),
    ],
    glyph: '📡',
    blurb: lt(
      'Radiostation om andere overlevenden te vinden.',
      'Broadcast station to find other survivors.',
    ),
    kind: 'broadcast',
  },
  workshop: {
    id: 'workshop',
    name: lt('Werkplaats', 'Workshop'),
    cardSurtitles: [
      lt('Knutselhoek', 'Craft nook'),
      lt('Werkbank', 'Workbench'),
      lt('Machinewerkplaats', 'Machine shop'),
    ],
    glyph: '🔧',
    blurb: lt(
      'Hier wordt alles wat je nodig hebt gemaakt.',
      'For crafting tools and equipment.',
    ),
    kind: 'craft',
  },
  garage: {
    id: 'garage',
    name: lt('Garage', 'Garage'),
    cardSurtitles: [
      lt('Parkeerplek', 'Bike rack'),
      lt('Schuurtje', 'Vehicle shed'),
      lt('Voertuigenloods', 'Vehicle bay'),
    ],
    glyph: '🅿️',
    blurb: lt(
      'Een plek om je voertuigen te parkeren.',
      'A place to park your vehicles.',
    ),
    kind: 'garage',
  },
  training_yard: {
    id: 'training_yard',
    name: lt('Trainingsplein', 'Training yard'),
    cardSurtitles: [
      lt('Modderige ring', 'Mud ring'),
      lt('Obstacle lane', 'Obstacle lane'),
      lt('Spar post', 'Spar post'),
    ],
    glyph: '🏋️',
    blurb: lt(
      'Passieve XP en korte trainingssessies voor endurance.',
      'Passive XP and short endurance training sessions.',
    ),
    kind: 'training',
  },
  computer_lab: {
    id: 'computer_lab',
    name: lt('Computerlab', 'Computer lab'),
    cardSurtitles: [
      lt('Losse schermen', 'Loose monitors'),
      lt('Terminalbank', 'Terminal row'),
      lt('Serverhoek', 'Server nook'),
    ],
    glyph: '🖥️',
    blurb: lt(
      'Vier slots voor salvaged hardware — hacking jobs voor buit.',
      'Four slots for salvaged hardware—hacking jobs for loot.',
    ),
    kind: 'computer_lab',
  },
  market_stall: {
    id: 'market_stall',
    name: lt('Handelsplein', 'Market stall'),
    cardSurtitles: [
      lt('Linnen zeil', 'Canvas awning'),
      lt('Teller', 'Counter'),
      lt('Magazijnhoek', 'Stock corner'),
    ],
    glyph: '🏪',
    blurb: lt(
      'Stuur een Trader — verdient Stash € op tijdgebonden runs.',
      'Send a Trader—earn stash credits on timed runs.',
    ),
    kind: 'market',
  },
}

/** Hub grid order */
export const WORKSTATION_LIST: WorkstationDef[] = [
  WORKSTATIONS.base_station,
  WORKSTATIONS.stash,
  WORKSTATIONS.kitchen,
  WORKSTATIONS.well,
  WORKSTATIONS.infirmary,
  WORKSTATIONS.lodging,
  WORKSTATIONS.broadcast_station,
  WORKSTATIONS.workshop,
  WORKSTATIONS.garage,
  WORKSTATIONS.training_yard,
  WORKSTATIONS.computer_lab,
  WORKSTATIONS.market_stall,
]
